import { Injectable, inject } from '@angular/core';

import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { Observable, exhaustMap, tap } from 'rxjs';

import { ResidentInvite } from '../../+sc-workspace/features/residents/models/resident-invite.model';
import { ResidentsInviteService } from '../../+sc-workspace/features/residents/services/residents-invite.service';
import { OperationStatus } from '../../shared/state/operation-status.utils';
import { ResidentInviteStatus } from '../models/resident-invite-status.enum';

interface NeighborInviteRegisterState {
  inviteFetchStatus: OperationStatus;
  invite: ResidentInvite;
  signUpStatus: OperationStatus;
}

const initialState: NeighborInviteRegisterState = {
  inviteFetchStatus: 'idle',
  invite: null,
  signUpStatus: 'idle',
};

@Injectable()
export class NeighborInviteRegisterStore extends ComponentStore<NeighborInviteRegisterState> {
  private readonly residentsInviteService = inject(ResidentsInviteService);

  readonly inviteFetchStatus$ = this.select(({ inviteFetchStatus }) => inviteFetchStatus);
  readonly invite$ = this.select(({ invite }) => invite);

  readonly signUpStatus$ = this.select(({ signUpStatus }) => signUpStatus);

  readonly fetchInviteById = this.effect((inviteId$: Observable<string>) =>
    inviteId$.pipe(
      tap(() => this.patchState({ inviteFetchStatus: 'loading' })),
      exhaustMap(inviteId => {
        return this.residentsInviteService.getInviteById(inviteId).pipe(
          tapResponse(
            invite => this.inviteFetchSuccess(invite),
            () => this.patchState({ inviteFetchStatus: 'error' }),
          ),
        );
      }),
    ),
  );

  readonly signUp = this.effect((updatedInvite$: Observable<ResidentInvite>) =>
    updatedInvite$.pipe(
      tap(() => this.patchState({ signUpStatus: 'loading' })),
      exhaustMap(invite =>
        this.residentsInviteService.acceptNeighborInvite(invite).pipe(
          tapResponse(
            () => this.patchState({ signUpStatus: 'success' }),
            () => this.patchState({ signUpStatus: 'error' }),
          ),
        ),
      ),
    ),
  );

  private readonly inviteFetchSuccess = this.updater((state, invite: ResidentInvite) => ({
    ...state,
    invite,
    inviteFetchStatus: invite.status === ResidentInviteStatus.Sent ? 'success' : 'error',
  }));

  constructor() {
    super(initialState);
  }
}
